import { Inclusion } from '@/diagnostic/domain/Inclusion.ts';
import { RuleId } from '@/rule';

abstract class SelectionMode {
  readonly inclusion: Inclusion;
  readonly #exceptions: Set<string>;

  protected constructor(inclusion: Inclusion, exceptions: Set<string>) {
    this.inclusion = inclusion;
    this.#exceptions = exceptions;
  }

  abstract select(rules: RuleId[]): SelectionMode;
  abstract unselect(rules: RuleId[]): SelectionMode;
  abstract isRuleSelected(rule: RuleId): boolean;
  abstract isAllSelected(total: number): boolean;
  abstract isNoneSelected(total: number): boolean;
  abstract selectedCount(total: number): number;

  get exceptions(): RuleId[] {
    return [...this.#exceptions].map((exception) => RuleId.of(exception));
  }

  isException(rule: RuleId): boolean {
    return this.#exceptions.has(rule.value);
  }

  protected get exceptionsCount(): number {
    return this.#exceptions.size;
  }

  protected addExceptions(rules: RuleId[]): Set<string> {
    const updated = new Set(this.#exceptions);
    for (const rule of rules) {
      updated.add(rule.value);
    }
    return updated;
  }

  protected removeExceptions(rules: RuleId[]): Set<string> {
    const updated = new Set(this.#exceptions);
    for (const rule of rules) {
      updated.delete(rule.value);
    }
    return updated;
  }
}

class IncludeMode extends SelectionMode {
  private constructor(exceptions: Set<string>) {
    super(Inclusion.Include, exceptions);
  }

  static none(): IncludeMode {
    return new IncludeMode(new Set());
  }

  select(rules: RuleId[]): SelectionMode {
    return new IncludeMode(this.addExceptions(rules));
  }

  unselect(rules: RuleId[]): SelectionMode {
    return new IncludeMode(this.removeExceptions(rules));
  }

  isRuleSelected(rule: RuleId): boolean {
    return this.isException(rule);
  }

  isAllSelected(total: number): boolean {
    return this.exceptionsCount === total;
  }

  isNoneSelected(): boolean {
    return this.exceptionsCount === 0;
  }

  selectedCount(): number {
    return this.exceptionsCount;
  }
}

class ExcludeMode extends SelectionMode {
  private constructor(exceptions: Set<string>) {
    super(Inclusion.Exclude, exceptions);
  }

  static all(): ExcludeMode {
    return new ExcludeMode(new Set());
  }

  select(rules: RuleId[]): SelectionMode {
    return new ExcludeMode(this.removeExceptions(rules));
  }

  unselect(rules: RuleId[]): SelectionMode {
    return new ExcludeMode(this.addExceptions(rules));
  }

  isRuleSelected(rule: RuleId): boolean {
    return !this.isException(rule);
  }

  isAllSelected(): boolean {
    return this.exceptionsCount === 0;
  }

  isNoneSelected(total: number): boolean {
    return this.exceptionsCount === total;
  }

  selectedCount(total: number): number {
    return total - this.exceptionsCount;
  }
}

export class RulesSelection {
  readonly #total: number;
  readonly #mode: SelectionMode;

  private constructor(total: number, mode: SelectionMode) {
    this.#total = total;
    this.#mode = mode;
  }

  static of(total: number): RulesSelection {
    if (total < 0) {
      throw new Error(`Total '${total}' is lower than 0`);
    }
    return new RulesSelection(total, ExcludeMode.all());
  }

  private ruleIds(rules: (RuleId | string)[]): RuleId[] {
    return rules.map((rule) => RuleId.of(rule));
  }

  select(...rules: (RuleId | string)[]): RulesSelection {
    return new RulesSelection(this.#total, this.#mode.select(this.ruleIds(rules)));
  }

  unselect(...rules: (RuleId | string)[]): RulesSelection {
    return new RulesSelection(this.#total, this.#mode.unselect(this.ruleIds(rules)));
  }

  toggle(rule: RuleId | string): RulesSelection {
    const id = RuleId.of(rule);
    return this.isRuleSelected(id) ? this.unselect(id) : this.select(id);
  }

  selectAll(): RulesSelection {
    return new RulesSelection(this.#total, ExcludeMode.all());
  }

  unselectAll(): RulesSelection {
    return new RulesSelection(this.#total, IncludeMode.none());
  }

  get inclusion(): Inclusion {
    return this.#mode.inclusion;
  }

  get exceptions(): RuleId[] {
    return this.#mode.exceptions;
  }

  get total(): number {
    return this.#total;
  }

  get count(): number {
    return this.#mode.selectedCount(this.#total);
  }

  get isAllSelected(): boolean {
    return this.#mode.isAllSelected(this.#total);
  }

  get isNoneSelected(): boolean {
    return this.#mode.isNoneSelected(this.#total);
  }

  isRuleSelected(id: RuleId | string): boolean {
    return this.#mode.isRuleSelected(RuleId.of(id));
  }
}
